/**
 * Main App Component
 * Handles routing and authentication state
 */

import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { useLanguagePreference } from '@/hooks/useUser'
import { useExtensionSessionSync } from '@/hooks/useExtensionSessionSync'
import { logger } from '@/lib/logger'
import { ProtectedRoute } from '@/components/auth'
import { analytics } from '@/lib/analytics'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'
import { CookieConsentBanner } from '@/components/cookie-consent'

// Lazy load pages for code splitting and better performance
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage }))
)
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const TransformPage = lazy(() =>
  import('@/pages/TransformPage').then((m) => ({ default: m.TransformPage }))
)
const AuthPage = lazy(() => import('@/pages/ClerkAuthPage').then((m) => ({ default: m.AuthPage })))
const SSOCallbackPage = lazy(() =>
  import('@/pages/SSOCallbackPage').then((m) => ({ default: m.SSOCallbackPage }))
)
const AuthCallbackPage = lazy(() =>
  import('@/pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage }))
)
const VerifyEmailPage = lazy(() =>
  import('@/pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage }))
)
const UpdatePasswordPage = lazy(() =>
  import('@/pages/UpdatePasswordPage').then((m) => ({ default: m.UpdatePasswordPage }))
)
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage }))
)
const UpgradePage = lazy(() =>
  import('@/pages/UpgradePage').then((m) => ({ default: m.UpgradePage }))
)
const SubscriptionSuccessPage = lazy(() =>
  import('@/pages/SubscriptionSuccessPage').then((m) => ({ default: m.SubscriptionSuccessPage }))
)
const HistoryPage = lazy(() =>
  import('@/pages/HistoryPage').then((m) => ({ default: m.HistoryPage }))
)
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)
const HelpPage = lazy(() => import('@/pages/HelpPage').then((m) => ({ default: m.HelpPage })))
const TermsOfServicePage = lazy(() =>
  import('@/pages/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage }))
)
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage }))
)
const ChromeRedirectPage = lazy(() =>
  import('@/pages/ChromeRedirectPage').then((m) => ({ default: m.ChromeRedirectPage }))
)
const BlogListPage = lazy(() =>
  import('@/pages/BlogListPage').then((m) => ({ default: m.BlogListPage }))
)
const BlogPostPage = lazy(() =>
  import('@/pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage }))
)
const BlogCategoryPage = lazy(() =>
  import('@/pages/BlogCategoryPage').then((m) => ({ default: m.BlogCategoryPage }))
)

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="space-y-4 text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  )
}

function AnalyticsListener() {
  const location = useLocation()
  const { isSignedIn, userId } = useAuth()

  // Track SPA route changes as page views
  useEffect(() => {
    analytics.page(location.pathname)
  }, [location.pathname])

  // Identify authenticated users so events attach to a person
  useEffect(() => {
    if (isSignedIn && userId) {
      analytics.identify(userId, {
        // Add any additional user properties here if needed
      })
    } else {
      analytics.reset()
    }
  }, [isSignedIn, userId])

  return null
}

function App() {
  const { isSignedIn, userId } = useAuth()
  const { i18n } = useTranslation()
  const savedLanguage = useLanguagePreference() as string | undefined
  const { hasConsented, consent } = useCookieConsentStore()

  // Sync session token to cookie for Chrome extension access
  useExtensionSessionSync()

  // Initialize analytics based on stored consent (run once on mount)
  useEffect(() => {
    if (hasConsented && consent.analytics) {
      analytics.initialize()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load user's saved language preference when they authenticate
  useEffect(() => {
    if (isSignedIn && userId && savedLanguage && savedLanguage !== i18n.language) {
      void i18n
        .changeLanguage(savedLanguage)
        .then(() => {
          logger.info('Loaded user language preference', { language: savedLanguage })
        })
        .catch((error) => {
          logger.error('Failed to change language', error instanceof Error ? error : undefined)
        })
    }
  }, [isSignedIn, userId, savedLanguage, i18n])

  return (
    <BrowserRouter>
      <AnalyticsListener />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/sso-callback" element={<SSOCallbackPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/chrome" element={<ChromeRedirectPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transform"
            element={
              <ProtectedRoute>
                <TransformPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <HistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upgrade"
            element={
              <ProtectedRoute>
                <UpgradePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            }
          />

          {/* Root route - landing page accessible to all users */}
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </Suspense>
      <CookieConsentBanner />
    </BrowserRouter>
  )
}

export default App
